// filepath: apps/web/src/features/cabinet/chrome/LanguageMenu.tsx
//
// FR-UI-CABINET-2 AC-3 - language switcher dropdown.
//
// The three supported locales (ru / kk / en, per FR-I18N-1) are
// shown in the menu with their native-language label. Labels are
// sourced from the i18n bundle (`cabinet.languageMenu.label.*`) so
// the chrome contains zero non-ASCII literals (satisfies the
// FR-UI-CORE-8 static guard for non-i18n files).
//
// Selecting an option:
//   1. Persists via `useLocalePreference().setLocale` (localStorage).
//   2. Calls `i18n.changeLanguage` to swap the resource bundle.
//   3. setDocumentLocale updates `<html lang>` (WCAG 2.4.2 / 3.1.1).
//
// The dropdown is a controlled component - the parent (Topbar) owns
// the open/closed state and exposes it via props.

import React, { useCallback, useEffect, useRef } from "react";
import i18n from "@/i18n/i18n";
import { useTranslation } from "@/i18n/useTranslation";
import { NEUTRAL, SEMANTIC, SPACE, RADIUS_MD } from "../styles";
import { useLocalePreference } from "./useLocalePreference";
import type { SupportedLocale } from "./i18nLocalePreference";

export interface LanguageMenuProps {
  open: boolean;
  onClose: () => void;
}

const OPTIONS: ReadonlyArray<{ value: SupportedLocale; i18nKey: string }> = [
  { value: "en", i18nKey: "cabinet.languageMenu.label.en" },
  { value: "ru", i18nKey: "cabinet.languageMenu.label.ru" },
  { value: "kk", i18nKey: "cabinet.languageMenu.label.kk" },
];

export function LanguageMenu({
  open,
  onClose,
}: LanguageMenuProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { setLocale } = useLocalePreference();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (
        target instanceof Node &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        onClose();
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const handleSelect = useCallback(
    (next: SupportedLocale) => {
      setLocale(next);
      onClose();
    },
    [setLocale, onClose],
  );

  if (!open) return null;

  const active = (i18n.language as SupportedLocale) ?? "ru";

  return (
    <div
      ref={containerRef}
      role="menu"
      aria-label="Language"
      data-testid="topbar-language-menu-panel"
      style={{
        position: "absolute",
        top: "calc(100% + 4px)",
        right: 0,
        minWidth: "160px",
        background: NEUTRAL.surfaceCard,
        border: `1px solid ${NEUTRAL.borderDefault}`,
        borderRadius: RADIUS_MD,
        boxShadow: "var(--shadow-md)",
        padding: `${SPACE[2]} 0`,
        zIndex: 100,
      }}
    >
      {OPTIONS.map((opt) => {
        const isActive = opt.value === active;
        return (
          <button
            key={opt.value}
            type="button"
            role="menuitemradio"
            aria-checked={isActive}
            aria-label={opt.i18nKey}
            data-testid={`lang-option-${opt.value}`}
            onClick={() => handleSelect(opt.value)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: `${SPACE[2]} ${SPACE[4]}`,
              background: isActive ? "#eff6ff" : "transparent",
              color: isActive ? SEMANTIC.primaryPressed : NEUTRAL.textSecondary,
              border: 0,
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
              fontSize: "14px",
              fontWeight: isActive ? 600 : 400,
              textAlign: "left",
            }}
          >
            <span>{t(opt.i18nKey, opt.value)}</span>
            {isActive ? (
              <span
                aria-hidden="true"
                style={{ color: SEMANTIC.primary, fontSize: "12px" }}
              >
                &#x2713;
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
